import { Component, AfterViewInit, inject, signal, computed, effect, ElementRef, viewChild, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { DeviceService } from './services/device.service';
import { MapService } from './services/map.service';
import { PrimeNGModule } from '../../config/primeng';
import { DashboardDataService } from './services/dashboard-data.service';
import { DashboardService } from '../../core/services/dashboard.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SystemStatsComponent } from './widgets/system-stats/system-stats.component';
import { LatestAlarmsComponent } from './widgets/latest-alarms/latest-alarms.component';
import { PowerAnalysisComponent } from './widgets/power-analysis/power-analysis.component';
import { ContractCapacityComponent } from './widgets/contract-capacity/contract-capacity.component';
import { AirQualityComponent } from './widgets/air-quality/air-quality.component';
import { MeetingRoomsComponent } from './widgets/meeting-rooms/meeting-rooms.component';
import { ROOM_COORDINATES } from './data/room-coordinates.data';
import { HasPermissionDirective } from '@/app/core/directives/has-permission.directive';
import { environment } from '../../../environments/environment';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    PrimeNGModule,
    SystemStatsComponent,
    LatestAlarmsComponent,
    PowerAnalysisComponent,
    ContractCapacityComponent,
    AirQualityComponent,
    MeetingRoomsComponent,
    HasPermissionDirective
  ],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
  host: { 'class': 'block h-full w-full' }
})
export class DashboardComponent implements AfterViewInit, OnDestroy {
  private deviceService = inject(DeviceService);
  private mapService = inject(MapService);
  private dataService = inject(DashboardDataService);
  private dashboardApiService = inject(DashboardService);
  private sanitizer = inject(DomSanitizer);
  private messageService = inject(MessageService);

  mapContainer = viewChild<ElementRef>('mapContainer');

  currentFloor = signal(14);
  floorOptions = [
    { label: '14F', value: 14 },
    { label: '15F', value: 15 }
  ];
  selectedSystem = signal('DEVICE');
  displayGroupControl = signal(false);
  displayVnc = signal(false);
  isVncMaximized = signal(false);
  isEditable = signal(false);

  vncUrl = computed<SafeResourceUrl>(() =>
    this.sanitizer.bypassSecurityTrustResourceUrl(`http://${environment.vncHost}:8080/vnc.html?host=${environment.vncHost}&port=6080&resize=scale`)
  );

  // 監控各樓層是否有設備告警
  floorAlarmStatus = computed(() => {
    const devices = this.devices();
    const status: Record<number, boolean> = { 14: false, 15: false };

    devices.forEach(d => {
      if (d.status === 'ALARM') {
        status[d.floor] = true;
      }
    });

    return status;
  });

  // Group Control Confirm Dialog
  displayConfirmDialog = signal(false);
  confirmData = signal<{ floorStr: string, categoryName?: string } | null>(null);

  devices = this.deviceService.devices;

  floorDevices = computed(() =>
    this.devices().filter(d => d.floor === this.currentFloor())
  );

  activeFilters = signal<string[]>([]);

  visibleDevices = computed(() => {
    const filters = this.activeFilters();
    const devices = this.floorDevices();
    if (filters.length === 0) return devices;
    return devices.filter(d => {
      if (filters.includes(d.type)) return true;
      if (filters.includes('MEETING') && ['RB', 'PANEL', 'HOST'].includes(d.type)) return true;
      return false;
    });
  });

  // Derived signals from DataService
  groupControlData = this.dataService.getGroupControlData(this.devices);
  filterOptions = this.dataService.getFilterOptions(this.floorDevices);
  systemStats = this.dataService.getSystemStats(this.devices);
  latestAlarms = this.dataService.getLatestAlarms(this.devices);

  // API Data
  powerAnalysisData = toSignal(
    timer(0, 15 * 60 * 1000).pipe(
      switchMap(() => this.dashboardApiService.apiGetPowerAnalysis())
    )
  );

  // Static Data
  airQuality = this.dataService.getAirQualityData(this.devices);
  meetingRooms = this.dataService.meetingRooms;

  constructor() {
    effect(() => {
      this.mapService.updateMapImage(this.currentFloor());
      this.mapService.updateMarkers(this.visibleDevices(), this.isEditable());
      this.mapService.updateMeetingRoomOverlays(ROOM_COORDINATES, this.currentFloor(), this.devices());
      this.mapService.processPendingHighlight();
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const container = this.mapContainer();
      if (container) {
        this.mapService.initMap(container, this.floorDevices(), ROOM_COORDINATES, this.currentFloor(), this.isEditable());
      }
    }, 100);
  }

  ngOnDestroy() {
    this.mapService.destroyMap();
  }

  showGroupControlDialog() {
    this.displayGroupControl.set(true);
  }

  openVnc() {
    this.displayVnc.set(true);
  }

  openMeetingRoomPortal() {
    window.open('https://outlook.office365.com', '_blank');
  }

  confirmTurnOffFloor(floorStr: string) {
    this.confirmData.set({ floorStr });
    this.displayConfirmDialog.set(true);
  }

  turnOffFloor(floorStr: string) {
    const floorNum = parseInt(floorStr, 10);
    this.deviceService.turnOffFloor(floorNum);
    this.messageService.add({
      severity: 'success',
      summary: '關閉設定成功',
      detail: `${floorStr} 所有設備已關閉設定`
    });
  }

  confirmTurnOffDeviceCategory(floorStr: string, categoryName: string) {
    this.confirmData.set({ floorStr, categoryName });
    this.displayConfirmDialog.set(true);
  }

  turnOffDeviceCategory(floorStr: string, categoryName: string) {
    const floorNum = parseInt(floorStr, 10);
    let type = '';
    if (categoryName === '照明設備') { type = 'LIGHT'; }
    else if (categoryName === '冷氣機設備') { type = 'AC'; }
    else if (categoryName === '全熱交換器設備') { type = 'FAN'; }

    if (type) {
      this.deviceService.turnOffDeviceCategory(floorNum, type);
      this.messageService.add({
        severity: 'success',
        summary: '關閉設定成功',
        detail: `${floorStr} ${categoryName}已關閉設定`
      });
    }
  }

  executeTurnOff() {
    const data = this.confirmData();
    if (!data) return;

    if (data.categoryName) {
      this.turnOffDeviceCategory(data.floorStr, data.categoryName);
    } else {
      this.turnOffFloor(data.floorStr);
    }

    this.displayConfirmDialog.set(false);
    this.confirmData.set(null);
  }

  cancelTurnOff() {
    this.displayConfirmDialog.set(false);
    this.confirmData.set(null);
  }

  getConfirmMessage() {
    const data = this.confirmData();
    if (!data) return '';
    return data.categoryName ? `${data.floorStr} ${data.categoryName}` : `${data.floorStr}`;
  }

  toggleFilter(type: string) {
    const currentFilters = this.activeFilters();
    if (currentFilters.length === 0) {
      this.activeFilters.set([type]);
    } else {
      if (currentFilters.includes(type)) {
        this.activeFilters.set(currentFilters.filter(f => f !== type));
      } else {
        this.activeFilters.set([...currentFilters, type]);
      }
    }
  }

  onAlarmClick(alarm: any) {
    if (alarm.deviceName) {
      const cleanName = alarm.deviceName.replace(/\(群組\)|（群組）/g, '').trim();
      const device = this.devices().find(d => d.name === cleanName);
      if (device) {
        if (device.floor !== this.currentFloor()) {
          this.mapService.setPendingHighlight(device.id);
          this.currentFloor.set(device.floor);
        } else {
          this.mapService.highlightMarker(device.id);
        }
      }
    }
  }

  exportDeviceConfig() {
    const staticConfig = this.devices().map(d => ({
      id: d.id,
      name: d.name,
      code: d.code,
      type: d.type,
      status: 'OFFLINE',
      floor: d.floor,
      position: d.position,
      angle: d.angle
    }));

    // 將最後的座標暫存到 localStorage 中
    localStorage.setItem('map-device-config', JSON.stringify(staticConfig));

    console.group('設備靜態設定檔匯出 (請複製以下陣列替換 device-config.data.ts 內容)');
    console.log(JSON.stringify(staticConfig, null, 4));
    console.log('// 已為您自動暫存至 localhost，即使重新整理網頁也會保留此座標設定！');
    console.groupEnd();
    alert('設定檔已暫存至本地，並輸出至瀏覽器 Console。\n(即使重整網頁也會保留最後匯出的位置喔！)');
  }
}

